import { FC, useMemo } from "react";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import uuidService from "../../../../../common/utils/uuid/uuid.service";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerTaxiDetailsWithoutDriverTableRow from "../common/types/order-optimizer-taxi-details-without-driver-table-row";
import orderOptimizerTaxiDetailsWithoutDriverTableHelper from "./order-optimizer-taxi-details-without-driver-table.helper";
import {
  OrderOptimizerTaxiDetailsItemOptimizedStats,
  OrderOptimizerTaxiDetailsItemUnoptimizedStats,
} from "../common/types/order-optimizer-taxi-details-item";
import orderOptimizerTaxiDetailsHelper from "../order-optimizer-taxi-details.helper";

type OrderOptimizerTaxiDetailsWithoutDriverTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsOptimizedStatsItem?: OrderOptimizerTaxiDetailsItemOptimizedStats;
  detailsUnoptimizedStatsItem?: OrderOptimizerTaxiDetailsItemUnoptimizedStats;
};

const OrderOptimizerTaxiDetailsWithoutDriverTableComponent: FC<
  OrderOptimizerTaxiDetailsWithoutDriverTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerTaxiDetailsWithoutDriverTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createOptimizedStatsTableRow = (
    detailsOptimizedStatsItem: OrderOptimizerTaxiDetailsItemOptimizedStats,
  ): OrderOptimizerTaxiDetailsWithoutDriverTableRow => {
    const distanceLabel = orderOptimizerTaxiDetailsHelper.getDistanceLabel(
      detailsOptimizedStatsItem.distance,
    );
    const distanceTitle = orderOptimizerTaxiDetailsHelper.getDistanceLabel(
      detailsOptimizedStatsItem.distance,
    );

    const contractRateLabel = orderOptimizerTaxiDetailsHelper.getRateLabel(
      detailsOptimizedStatsItem.contractRate,
    );
    const contractRateTitle = orderOptimizerTaxiDetailsHelper.getRateLabel(
      detailsOptimizedStatsItem.contractRate,
    );

    const totalTimeLabel = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.totalTime,
    );
    const totalTimeTitle = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.totalTime,
    );

    const haltingTimeLabel = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOptimizedStatsItem.haltingTime,
    );

    const marginLabel = orderOptimizerTaxiDetailsHelper.getMarginLabel(
      detailsOptimizedStatsItem.margin,
    );
    const marginTitle = orderOptimizerTaxiDetailsHelper.getMarginLabel(
      detailsOptimizedStatsItem.margin,
    );

    const translations =
      orderOptimizerTranslationsHelper.getTaxiDetailsTranslations().table
        .withoutDriver;

    return {
      id: uuidService.generate(),
      value: {
        withoutDriver: (
          <div title={translations.routeTitle}>{translations.routeLabel}</div>
        ),
        billingModel: <div title="ABA">ABA</div>,
        contractRate: <div title={contractRateTitle}>{contractRateLabel}</div>,
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={
              detailsOptimizedStatsItem.haltingTime < 0 ? "time_delayed" : ""
            }
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: <div title={marginTitle}>{marginLabel}</div>,
      },
    };
  };

  const createUnoptimizedStatsTableRow = (
    detailsUnoptimizedStatsItem: OrderOptimizerTaxiDetailsItemUnoptimizedStats,
  ): OrderOptimizerTaxiDetailsWithoutDriverTableRow => {
    const distanceLabel = orderOptimizerTaxiDetailsHelper.getDistanceLabel(
      detailsUnoptimizedStatsItem.distance,
    );
    const distanceTitle = orderOptimizerTaxiDetailsHelper.getDistanceLabel(
      detailsUnoptimizedStatsItem.distance,
    );

    const totalTimeLabel = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.totalTime,
    );
    const totalTimeTitle = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.totalTime,
    );

    const haltingTimeLabel = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsUnoptimizedStatsItem.haltingTime,
    );

    const contractRateLabel = orderOptimizerTaxiDetailsHelper.getRateLabel(
      detailsUnoptimizedStatsItem.contractRate,
    );
    const contractRateTitle = orderOptimizerTaxiDetailsHelper.getRateLabel(
      detailsUnoptimizedStatsItem.contractRate,
    );

    const marginLabel = orderOptimizerTaxiDetailsHelper.getMarginLabel(
      detailsUnoptimizedStatsItem.margin,
    );
    const marginTitle = orderOptimizerTaxiDetailsHelper.getMarginLabel(
      detailsUnoptimizedStatsItem.margin,
    );

    const translations =
      orderOptimizerTranslationsHelper.getTaxiDetailsTranslations().table
        .withoutDriver;

    return {
      id: uuidService.generate(),
      value: {
        withoutDriver: (
          <div title={translations.sumOfOrdersTitle}>
            {translations.sumOfOrdersLabel}
          </div>
        ),
        billingModel: <div title="ABA">ABA</div>,
        contractRate: <div title={contractRateTitle}>{contractRateLabel}</div>,
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={
              detailsUnoptimizedStatsItem.haltingTime < 0 ? "time_delayed" : ""
            }
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: <div title={marginTitle}>{marginLabel}</div>,
      },
    };
  };

  const rows: OrderOptimizerTaxiDetailsWithoutDriverTableRow[] = useMemo(() => {
    if (!props.detailsOptimizedStatsItem || !props.detailsUnoptimizedStatsItem)
      return [];

    const optimizedRows = createOptimizedStatsTableRow(
      props.detailsOptimizedStatsItem,
    );

    const unoptimizedRows = createUnoptimizedStatsTableRow(
      props.detailsUnoptimizedStatsItem,
    );

    return [unoptimizedRows, optimizedRows];
  }, [
    props.detailsUnoptimizedStatsItem,
    props.detailsOptimizedStatsItem,
    selectedAppLanguage,
  ]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerTaxiDetailsWithoutDriverTableComponent;
